import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';

import { Container, UploadItems, UploadFile, UploadFileName, UploadFileSize, UploadFileNameLabel, FilesList, FileItem, FileItemLabel, FileItemValue, UploadFileStatus, UploadProgress, UploadProgressText, FilemanagerSummary, FilemanagerBlock, FilemanagerBlockTitle, FilemanagerBlockValue, FilemanagerButton } from './ResourceUploader.styles';
import { uploadFiles, getFiles, removeFiles } from '../../../services/resources';
import T from '../../i18n/i18n';

const ReadableSize = (sizeInt) => {
    if (sizeInt > 1048576) {
        return (sizeInt/1048576).toFixed(1) + 'MB'
    } else if (sizeInt > 1024) {
        return (sizeInt/1024).toFixed(1) + 'KB'
    } else {
        return sizeInt + 'Bytes';
    }
};

export default forwardRef(({defaultValue, onSelected, hideTypeColumn, mode}, ref) => {

    const routerParams = useParams();

    const [fileLoadedList, setFileLoadedList] = useState([]);
    const [fileList, setFileList] = useState([]);
    const [fileNameList, setFileNameList] = useState([]);
    const [filesSelected, setFilesSelected] = useState([]);
    const [filesStats, setFilesStats] = useState({});
    const [uploadRunning, setUploadRunning] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('waiting');
    const [uploadMessage, setUploadMessage] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    
    useImperativeHandle(ref, () => ({
        handleFileLoading: (files) => {
            const f = [];
            const fileNames = [];
            for (let i=0; i<files.length; i++) {
                f.push({
                    file: files[i],
                    name: files[i].name,
                    size: ReadableSize(files[i].size)
                });
                fileNames.push(files[i].name);
            }
    
            setUploadRunning(false);
            setFileLoadedList(f);
            setFileNameList(fileNames);
        },

        removeSelectedFiles: async () => {
            let files = await removeFiles(routerParams.id, filesSelected);
            setFileList(files.files || []);
            setFilesStats({
                countTotal: files.countTotal || 0,
                sizeTotal: ReadableSize(files.sizeTotal || 0),
                count: files.count || '0',
                size: ReadableSize(files.size || 0),
            });
            setFilesSelected([]);
        },

        finish: (callback) => {
            callback(filesSelected);
            setFilesSelected([]);
        }
    }));

    useEffect(() => {
        if (defaultValue) {
            try {
                const file = JSON.parse(defaultValue);
                if (file[0] && file[0].id && file[0].name) {
                    setFilesSelected(file);
                }
            } catch (e) {
                console.error(Object.assign(e,{component:'ResourceUploader', id: 'parseResourceValue'}));
            }
        }
    }, [defaultValue]);

    useEffect(() => {
        if (typeof onSelected === 'function') {
            onSelected(filesSelected);
        }
    }, [onSelected, filesSelected]);


    useEffect(() => {
        let shouldUpdate = true;
        (async () => {
            let files = await getFiles(routerParams.id);
            
            if (!shouldUpdate) {
                return;
            }

            setFileList(files.files || []);
            setFilesStats({
                countTotal: files.countTotal || 0,
                sizeTotal: ReadableSize(files.sizeTotal || 0),
                count: files.count || '0',
                size: ReadableSize(files.size || 0),
            });
        })();

        return () => {
            shouldUpdate = false;
        }
    }, [routerParams]);


    const getFilenameValue = (index) => {
        return fileNameList[index] || '';
    };

    const setFilenameValue = (index, value) => {
        setFileNameList(items => {
            let result = [...items];
            result[index] = value;
            return result;
        });
    };

    const confirmUpload = () => {
        setUploadRunning(true);
        setUploadStatus('waiting');
        setUploadMessage('');
        uploadFiles(fileLoadedList, fileNameList, routerParams.id, (progress) => {
            setUploadProgress(progress);
        }).then((data) => {
            let files = data.data.payload;
            setUploadStatus('ok');
            setUploadMessage('');
            setFileList(files.files || []);
            setFilesStats({
                countTotal: files.countTotal || 0,
                sizeTotal: ReadableSize(files.sizeTotal || 0),
                count: files.count || '0',
                size: ReadableSize(files.size || 0),
            });
            setTimeout(() => {
                setUploadRunning(false);
                setFileLoadedList([]);
                setFileNameList([]);
            }, 2000);
        }).catch((error) => {
            setUploadStatus('error');
            if (error.response && error.response.data && error.response.data.payload)
            setUploadMessage(': ' + (error.response.data.payload || ''));
        });
    };

    const handleFileSelect = (id, name) => {
        setFilesSelected(files => {
            let result = [...files];
            let index = result.map(i => i.id).indexOf(id);

            if (index >= 0) {
                result.splice(index, 1);
            } else {
                if (mode === 'single') {
                    result = [{id, name}];
                } else {
                    result.push({id, name});
                }
            }

            return result;
        });
    };
    
    return <Container>
        {fileLoadedList.length <= 0 && <FilesList className="scrollbar">
            <FileItem>
                <FileItemLabel size="3"><T value="filemanager.filename"/></FileItemLabel>
                {hideTypeColumn ? null : <FileItemLabel size="1"><T value="filemanager.filetype"/></FileItemLabel>}
                <FileItemLabel size="1"><T value="filemanager.size"/></FileItemLabel>
            </FileItem>
            {fileList && fileList.map((file,i) => <FileItem key={i} className={filesSelected.map(i=>i.id).indexOf(file.id) >= 0 ? 'selected' : ''} onClick={() => {handleFileSelect(file.id, file.name)}}>
                <FileItemValue size="3">{file.name}</FileItemValue>
                {hideTypeColumn ? null : <FileItemValue size="1">{file.mimetype}</FileItemValue>}
                <FileItemValue size="1">{ReadableSize(file.size)}</FileItemValue>
            </FileItem>)}
        </FilesList>}
        {fileLoadedList.length > 0 && <UploadItems className="scrollbar">
            {fileLoadedList && fileLoadedList.map((file, i) => <UploadFile key={i}>
                {!uploadRunning && <UploadFileName value={getFilenameValue(i)} onChange={(e) => {setFilenameValue(i, e.target.value)}} />}
                {uploadRunning && <UploadFileNameLabel>{getFilenameValue(i)}</UploadFileNameLabel>}
                <UploadFileSize>{file.size}</UploadFileSize>
            </UploadFile>)}
            {fileLoadedList.length > 0 && !uploadRunning && <FilemanagerButton color="#0c0" hoverColor={"#000"} onClick={() => {confirmUpload()}}><T value="filemanager.confirmUpeload" /></FilemanagerButton>}
            {uploadRunning && <UploadFileStatus className={uploadStatus}>
                <UploadProgress progress={uploadProgress}/>
                <UploadProgressText>
                    <T value={`filemanager.status.${uploadStatus}`} />
                    {uploadMessage}
                </UploadProgressText>
            </UploadFileStatus>}
        </UploadItems>}
        <FilemanagerSummary>
            <FilemanagerBlock>
                <FilemanagerBlockTitle><T value="filemanager.thisTable" /></FilemanagerBlockTitle>
                <FilemanagerBlockValue>{filesStats.count} <T value="filemanager.files" /> ({filesStats.size})</FilemanagerBlockValue>
            </FilemanagerBlock>
            <FilemanagerBlock>
                <FilemanagerBlockTitle><T value="filemanager.allTable" /></FilemanagerBlockTitle>
                <FilemanagerBlockValue>{filesStats.countTotal} <T value="filemanager.files" /> ({filesStats.sizeTotal})</FilemanagerBlockValue>
            </FilemanagerBlock>
        </FilemanagerSummary>
    </Container>
});